import { useCallback, useMemo, useState } from "react";
import { Button } from "@radix-ui/themes";
import { useQuery } from "react-query";

import { useReleaseConversationApi } from "@/v2/entities/release-conversation/ReleaseConversation.api";
import {
  ReleaseConversationFilters,
  ReleaseConversation,
} from "@/v2/entities/release-conversation/ReleaseConversation.model";
import { ExploreFiltersValue } from "@/v2/features/explore-filter-menu/ExploreFilterMenu";
import { Filter } from "@/v2/entities/explore/Explore.filter";
import { Topic } from "@/v2/entities/topic/Topic.model";
import { InteractionType } from "@/routes/simulator-live-mode/types";

import { useSimulator } from "../../context/SimulatorWrapper";

import { TrainingPanelInteractionCard } from "./TrainingPanelInteractionCard";
import { InteractionListView, InteractionListItems } from "./shared";

interface SessionsViewProps {
  selectedTopic: Topic | null;
  topicIds: number[];
  onNavigateToTranscript: (
    interactionId: string,
    fetchTranscriptId: string,
    interactionType: InteractionType,
    pendingReviewId?: number
  ) => void;
  onResetNavigation: () => void;
  activeFilters: ExploreFiltersValue;
  setActiveFilters: (filters: ExploreFiltersValue) => void;
  sharedFilter: Filter | undefined;
  search: string;
  onSearchChanged: (search: string) => void;
}

export const SessionsView = ({
  selectedTopic,
  topicIds,
  onNavigateToTranscript,
  onResetNavigation,
  activeFilters,
  setActiveFilters,
  sharedFilter,
  search,
  onSearchChanged,
}: SessionsViewProps) => {
  const [page, setPage] = useState(1);
  const limit = 50;
  const releaseConversationApi = useReleaseConversationApi();
  const { releasesWithVersions, topics } = useSimulator();

  const activeFiltersTopicIds = useMemo(() => {
    if (!activeFilters.topic || !topics) return [];

    const topicFilter = activeFilters.topic;

    if (topicFilter.operator === "in" && Array.isArray(topicFilter.value)) {
      return (
        topics
          ?.filter((topic) => topicFilter.value.includes(topic.topic))
          .map((topic) => topic.id) || []
      );
    }

    if (topicFilter.operator === "eq") {
      return (
        topics
          ?.filter((topic) => topic.topic === topicFilter.value)
          .map((topic) => topic.id) || []
      );
    }

    return [];
  }, [activeFilters, topics]);

  const filters: ReleaseConversationFilters = useMemo(() => {
    const last20Releases = releasesWithVersions?.slice(-20) || [];
    const baseFilters: ReleaseConversationFilters = {
      page,
      limit,
      releaseIds: last20Releases?.map((r) => r.id) || [],
      topicIds:
        activeFiltersTopicIds.length > 0 ? activeFiltersTopicIds : undefined,
    };

    if (search) {
      baseFilters.commentFilter = search;
    }

    const sessionVersionFilter = activeFilters.sessionVersion as any;
    if (
      sessionVersionFilter &&
      sessionVersionFilter.value &&
      Array.isArray(sessionVersionFilter.value) &&
      sessionVersionFilter.value.length > 0
    ) {
      const selectedReleaseIds =
        releasesWithVersions
          ?.filter((r) =>
            sessionVersionFilter.value.includes(r.version.toString())
          )
          .map((r) => r.id) || [];
      if (selectedReleaseIds.length > 0) {
        baseFilters.releaseIds = selectedReleaseIds;
      }
    }

    const sessionInteractionTypeFilter =
      activeFilters.sessionInteractionType as any;
    if (
      sessionInteractionTypeFilter &&
      sessionInteractionTypeFilter.value &&
      Array.isArray(sessionInteractionTypeFilter.value) &&
      sessionInteractionTypeFilter.value.length > 0
    ) {
      if (sessionInteractionTypeFilter.value.includes("conversations")) {
        baseFilters.hasRelatedConversation = true;
      }
      if (sessionInteractionTypeFilter.value.includes("tickets")) {
        baseFilters.hasRelatedTicket = true;
      }
      if (sessionInteractionTypeFilter.value.includes("pending_review")) {
        baseFilters.hasRelatedTraining = true;
      }
    } else {
      baseFilters.hasRelatedConversation = undefined;
      baseFilters.hasRelatedTicket = undefined;
      baseFilters.hasRelatedTraining = undefined;
    }

    const quackUserIdFilter = activeFilters.quackUserId;
    if (
      quackUserIdFilter &&
      quackUserIdFilter.operator === "in" &&
      Array.isArray(quackUserIdFilter.value) &&
      quackUserIdFilter.value.length > 0
    ) {
      baseFilters.quackUserId = quackUserIdFilter.value[0];
    }

    const feedbackTypeFilter = activeFilters.feedbackType as any;
    if (
      feedbackTypeFilter &&
      feedbackTypeFilter.value &&
      Array.isArray(feedbackTypeFilter.value) &&
      feedbackTypeFilter.value.length > 0
    ) {
      baseFilters.feedbackType = feedbackTypeFilter.value;
    }

    return baseFilters;
  }, [
    page,
    limit,
    activeFiltersTopicIds,
    search,
    releasesWithVersions,
    activeFilters,
  ]);

  const { data: sessionsData, isLoading: isLoadingSessions } = useQuery({
    queryKey: ["release-conversations", filters, activeFilters],
    queryFn: () => releaseConversationApi.getReleaseConversations(filters),
    enabled: true,
  });

  const handleSessionClick = useCallback(
    (session: ReleaseConversation) => {
      onNavigateToTranscript(
        session.trainingSessionId,
        `session-${JSON.stringify(session)}`,
        InteractionType.CONVERSATION
      );
    },
    [onNavigateToTranscript]
  );

  const handleLoadMore = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  const hasMorePages = useMemo(() => {
    return (
      sessionsData?.pagination &&
      sessionsData.pagination.page < sessionsData.pagination.totalPages
    );
  }, [sessionsData?.pagination]);

  const getInteractionType = useCallback((session: ReleaseConversation) => {
    if (session.ticketId || session.ticketInTrainingId) {
      return "tickets";
    }
    if (session.conversationId || session.conversationInTrainingId) {
      return "conversations";
    }
    return null;
  }, []);

  const isPendingReview = useCallback((session: ReleaseConversation) => {
    return !!(session.ticketInTrainingId || session.conversationInTrainingId);
  }, []);

  const customFilterOptions = useMemo(
    () => ({
      sessionVersion:
        releasesWithVersions?.map((release) => ({
          label: `Version ${release.version}`,
          value: release.version.toString(),
        })) || [],
    }),
    [releasesWithVersions]
  );

  const quackUserIdOptions = useMemo(() => {
    if (!sessionsData?.conversations) return [];

    return sessionsData.conversations.reduce(
      (acc: Array<{ label: string; value: string }>, session) => {
        if (
          session.quackUserId &&
          !acc.find((opt) => opt.value === session.quackUserId)
        ) {
          acc.push({
            label: session.quackUserId,
            value: session.quackUserId,
          });
        }
        return acc;
      },
      []
    );
  }, [sessionsData?.conversations]);

  return (
    <InteractionListView
      activeFilters={activeFilters}
      setActiveFilters={setActiveFilters}
      filter={sharedFilter}
      isLoading={isLoadingSessions}
      isEmpty={
        !sessionsData?.conversations || sessionsData.conversations.length === 0
      }
      loadingText="Loading sessions..."
      emptyText="No sessions found"
      search={search}
      onSearchChanged={(value) => {
        onSearchChanged(value);
        setPage(1);
      }}
      showSearchAndFilters={true}
      allowedFilterFields={[
        "topic",
        "sessionVersion",
        "sessionInteractionType",
        "quackUserId",
        "feedbackType",
      ]}
      customFilterOptions={{
        ...customFilterOptions,
        quackUserId: quackUserIdOptions,
      }}
    >
      {sessionsData?.conversations && sessionsData.conversations.length > 0 && (
        <>
          <InteractionListItems>
            {sessionsData.conversations.map((session) => (
              <TrainingPanelInteractionCard
                key={session.id}
                interaction={{
                  interactionType: "conversations",
                  id: session.trainingSessionId,
                  displayId: session.quackUserId
                    ? `${session.id} By ${session.quackUserId}`
                    : `${session.id}`,
                  subject:
                    session.releaseComments?.[0]?.comment ||
                    session.feedbackComment ||
                    `Session ${session.trainingSessionId}`,
                  description:
                    session.releaseComments?.[0]?.comment ||
                    session.feedbackComment ||
                    `Session ${session.trainingSessionId}`,
                  topic: session?.topicId
                    ? topics?.find((t: Topic) => t.id === session.topicId)
                        ?.topic || ""
                    : "",
                  category: session.feedback !== "NONE" ? session.feedback : "",
                  createdAt: session.createdAt,
                  updatedAt: session.updatedAt,
                  status: "ACTIVE",
                  channel: "simulator",
                  countPublicMessages: session.releaseComments?.length || 0,
                  countInternalMessages: 0,
                }}
                submitter={`Version ${
                  releasesWithVersions?.find((r) => r.id === session.releaseId)
                    ?.version || ""
                }`}
                interactionType={getInteractionType(session)}
                onClick={() => handleSessionClick(session)}
                isPendingReview={isPendingReview(session)}
              />
            ))}
          </InteractionListItems>

          {hasMorePages && (
            <Button
              variant="soft"
              onClick={handleLoadMore}
              disabled={isLoadingSessions}
            >
              {isLoadingSessions ? "Loading..." : "Load More"}
            </Button>
          )}
        </>
      )}
    </InteractionListView>
  );
};
